<script>
  import { onDestroy, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Badge, Button, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { PersonCell } from '@client/pages/insights/activity/cell-formatters';

  import { extractEmails, generateUUID } from '@common/utils/strings';

  export let row = {};
  export let recipientFilter = null;

  const batchSize = 10;

  let visibleCount = 10;
  let sentinel;
  let observer;
  let showGrouped = false;
  let photoErrors = [];
  let loading = false;

  const onSelect = (email) => {
    recipientFilter = recipientFilter === email ? null : email;
  };

  const groupByEmailDomain = (recipients) => {
    const domainGroups = {};

    for (const key in recipients) {
      const email = recipients[key];
      const domain = email.split('@').pop();

      if (domainGroups[domain]) {
        domainGroups[domain].push(email);
      } else {
        domainGroups[domain] = [email];
      }
    }

    return Object.entries(domainGroups)
      .map(([domain, emails]) => {
        domainGroups[domain] = emails.sort();

        return [domain, emails];
      })
      .sort();
  };

  const updateSrc = function () {
    this.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  };

  const handleIntersect = (entries) => {
    if (entries[0].isIntersecting && !loading) {
      loading = true;
      visibleCount += batchSize;

      setTimeout(() => {
        loading = false;

        if (sentinel) {
          observer.observe(sentinel);
        }
      }, 100);
    }
  };

  const setupObserver = () => {
    if (observer) {
      observer.disconnect();
    }

    observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '300px 0px',
      threshold: 0.1
    });

    if (sentinel) {
      observer.observe(sentinel);
    }
  };

  onMount(setupObserver);

  onDestroy(() => observer && observer.disconnect());

  $: if (sentinel && observer) {
    observer.observe(sentinel);
  }

  $: additionalRecipients = [...extractEmails(row?.to), ...extractEmails(row?.cc), ...extractEmails(row?.bcc)];
  $: recipients = Array.from(new Set(Object.values(row.recips).concat(additionalRecipients)));
  $: hasRecipients = recipients.length > 0;
  $: grouped = groupByEmailDomain(recipients);
</script>

<div>
  <h3>
    People
    {#if hasRecipients}
      <Button size="sm" class="groupby" active={showGrouped} on:click={() => (showGrouped = !showGrouped)}
        >{$format('label.GROUP_BY_DOMAIN')}</Button
      >
    {/if}
  </h3>
  <ul class="recipients">
    {#if showGrouped}
      {#each grouped as [domain, emails]}
        <h4>
          {#if !photoErrors.includes(domain)}
            <div class="company-logo">
              <img
                src="https://logo.clearbit.com/{domain}"
                on:error={() => (photoErrors = [...photoErrors, domain])}
                on:load={updateSrc}
                loading="lazy"
                referrerpolicy="no-referrer"
                alt={domain}
                style="background: url(https://logo.clearbit.com/{domain}) no-repeat center center / contain; height: 20px;  width: 20px;"
              />
            </div>
          {/if}
          {domain}
          <div class="count">{emails.length}</div>
        </h4>
        {#each emails.slice(0, visibleCount) as email}
          {@const id = generateUUID()}
          <li class:last-active={email === row.lastactivity.recipient} class:filtering={recipientFilter === email}>
            <div id="filter-{id}" class="filter" role="button" tabindex="0" on:click={() => onSelect(email)}>
              <Icon name="filter" />
            </div>
            <Tooltip target="filter-{id}" placement="top">
              {#if recipientFilter !== email}
                {$format('label.FILTER_DATA_ON_RECIPIENT')}
              {:else}
                {$format('label.REMOVE_FILTERING_ON_RECIPIENT')}
              {/if}
            </Tooltip>
            <PersonCell {email} activity={row.activity} showCompanyLogo />
          </li>
        {/each}
      {/each}
    {:else}
      {#each recipients.sort().slice(0, visibleCount) as email}
        {@const id = generateUUID()}
        <li class:last-active={email === row.lastactivity.recipient} class:filtering={recipientFilter === email}>
          <div id="filter-{id}" class="filter" role="button" tabindex="0" on:click={() => onSelect(email)}>
            <Icon name="filter" />
          </div>
          <Tooltip target="filter-{id}" placement="top">
            {#if recipientFilter !== email}
              {$format('label.FILTER_DATA_ON_RECIPIENT')}
            {:else}
              {$format('label.REMOVE_FILTERING_ON_RECIPIENT')}
            {/if}
          </Tooltip>
          <PersonCell
            {email}
            activity={row.activity}
            trackingDisabled={row.notrack && row.notrack[email]}
            showCompanyLogo
          />
        </li>
      {/each}
    {/if}

    {#if !hasRecipients}
      <li>{$format('label.NO_RECIPIENT_ACTIVITY')}</li>
    {/if}
  </ul>

  <div bind:this={sentinel} class="load-more-trigger" />
</div>

<style lang="scss">
  .load-more-trigger {
    height: 1px;
  }

  h3 {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .recipients {
    h4 {
      align-items: center;
      display: flex;
      gap: 6px;
      margin: var(--margin-large) 0 var(--margin-base);

      .company-logo {
        background-color: var(--color-white);
        border: 1px solid var(--border-color);
        border-radius: 100%;
        padding: 2px;

        img {
          border-radius: 100%;
        }
      }

      .count {
        background-color: var(--color-gray-800);
        border-radius: 100%;
        color: var(--color-white);
        font-size: var(--font-size-small);
        line-height: 1.5em;
        padding: var(--padding-xsmall) var(--padding-small);
        text-align: center;
        width: 21px;
      }
    }

    li {
      background-color: var(--color-white);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-large);
      padding: var(--padding-large);
      position: relative;
      margin-top: var(--margin-large);

      .filter {
        display: none;
      }

      &:hover,
      &.filtering {
        .filter {
          align-items: center;
          background-color: var(--color-white);
          border: 1px solid var(--border-color);
          border-radius: 100%;
          bottom: 0;
          display: flex;
          height: 28px;
          justify-content: center;
          left: 0;
          margin: auto 0;
          position: absolute;
          width: 28px;
          transform: translateX(-50%) translateY(0);
          top: 0;

        }
      }

      &.filtering,
      &.last-active.filtering {
        border: 1px solid var(--color-green-400);
        box-shadow: 0 0 0 1px var(--color-green-400);

        .filter {
          background-color: var(--color-green-400);
          border: 1px solid var(--color-green-400);
          color: var(--color-white);
        }
      }

      &.last-active {
        border: 1px solid var(--color-blue-400);
        box-shadow: 0 0 0 1px var(--color-blue-400);
        position: relative;

        &:before {
          background-color: var(--color-blue-400);
          border-radius: var(--border-radius-base) 0 0 0;
          bottom: 0;
          color: var(--color-white);
          content: 'Last Activity';
          font-size: var(--font-size-xsmall);
          padding: var(--padding-small) 5px 2px 8px;
          position: absolute;
          right: 0;
          text-transform: uppercase;
        }
      }
    }
  }
</style>
